import React from "react";
import Modal from "react-bootstrap/Modal";
import {
  BsCheckCircle,
  BsExclamationTriangle,
  BsPersonExclamation,
  BsPersonFill,
  BsXCircle,
} from "react-icons/bs";
import { Alert, Badge, Button, Card, Col, Row } from "react-bootstrap";

const NurseActiveNotYetWorkedModal = ({ visible, close, nurseReport }) => {
  if (!nurseReport) return null;

  const openProfileInNewWindow = () => {
    const baseUrl = window.location.origin;
    const profileUrl = `${baseUrl}/users/${nurseReport.user.id}`;
    window.open(profileUrl, "_blank", "noopener,noreferrer");
  };

  const getErrorTypeDescription = (error) => {
    if (error.startsWith("CertsError:")) {
      return {
        type: "Certification Issues",
        icon: <BsXCircle className="me-2" />,
        variant: "danger",
        description: error.replace("CertsError: ", ""),
        solution:
          "Upload required certifications or contact administrator to verify mandatory certification requirements.",
      };
    } else if (error.startsWith("NMCError:")) {
      return {
        type: "NMC Registration",
        icon: <BsExclamationTriangle className="me-2" />,
        variant: "warning",
        description: error.replace("NMCError: ", ""),
        solution:
          "Add valid NMC registration number in the nurse's profile personal information section.",
      };
    } else if (error.startsWith("AxisStartError:")) {
      return {
        type: "Axis Start Date",
        icon: <BsExclamationTriangle className="me-2" />,
        variant: "info",
        description: error.replace("AxisStartError: ", ""),
        solution:
          "Set the Axis start date in the nurse's profile to indicate when they began working with the platform.",
      };
    }
    return {
      type: "Unknown Issue",
      icon: <BsExclamationTriangle className="me-2" />,
      variant: "secondary",
      description: error,
      solution: "Contact system administrator for assistance with this issue.",
    };
  };

  const getUserStatusBadge = (status) => {
    const statusColors = {
      ACTIVE: "success",
      ONHOLD: "warning",
      BLOCKED: "danger",
    };
    return <Badge bg={statusColors[status] || "secondary"}>{status}</Badge>;
  };

  const fullName = `${nurseReport.user.forename} ${nurseReport.user.surname}`;

  return (
    <Modal
      show={visible}
      onHide={close}
      size="lg"
      aria-labelledby="contained-modal-title-vcenter"
      centered
    >
      <Modal.Header closeButton>
        <Modal.Title
          id="contained-modal-title-vcenter"
          className="text-axis-offblack"
        >
          <BsPersonExclamation className="me-2" />
          Nurse Work Readiness Issues
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <Card className="mb-3">
          <Card.Header>
            <Row className="align-items-center">
              <Col>
                <h6 className="mb-0">
                  <BsPersonFill className="me-2" />
                  Nurse Information
                </h6>
              </Col>
            </Row>
          </Card.Header>
          <Card.Body>
            <Row>
              <Col md={6}>
                <strong>Name:</strong> {fullName}
              </Col>
              <Col md={6}>
                <strong>Email:</strong> {nurseReport.user.email}
              </Col>
            </Row>
            <Row className="mt-2">
              <Col md={6}>
                <strong>Status:</strong>{" "}
                {getUserStatusBadge(nurseReport.user.status)}
              </Col>
            </Row>
          </Card.Body>
        </Card>

        <Card>
          <Card.Header>
            <Row className="align-items-center">
              <Col>
                <h6 className="mb-0">
                  <BsExclamationTriangle className="me-2" />
                  Issues Preventing Work ({nurseReport.errors.length})
                </h6>
              </Col>
            </Row>
          </Card.Header>
          <Card.Body>
            {nurseReport.errors.length === 0 ? (
              <Alert variant="success">
                <BsCheckCircle className="me-2" />
                No issues found. This nurse is ready to work!
              </Alert>
            ) : (
              <div>
                <Alert variant="warning" className="mb-3">
                  <BsExclamationTriangle className="me-2" />
                  This nurse is currently <strong>active</strong> but has{" "}
                  <strong>{nurseReport.errors.length}</strong> issue
                  {nurseReport.errors.length !== 1 ? "s" : ""} that must be
                  resolved before they can work shifts.
                </Alert>

                {nurseReport.errors.map((error, index) => {
                  const errorInfo = getErrorTypeDescription(error);
                  return (
                    <Card key={index} className="mb-3">
                      <Card.Header>
                        <Badge bg={errorInfo.variant} className="me-2">
                          {errorInfo.icon}
                          {errorInfo.type}
                        </Badge>
                      </Card.Header>
                      <Card.Body>
                        <Row>
                          <Col>
                            <strong>Issue:</strong> {errorInfo.description}
                          </Col>
                        </Row>
                        <Row className="mt-2">
                          <Col>
                            <strong>Resolution:</strong> {errorInfo.solution}
                          </Col>
                        </Row>
                      </Card.Body>
                    </Card>
                  );
                })}
              </div>
            )}
          </Card.Body>
        </Card>
      </Modal.Body>

      <Modal.Footer>
        <Row className="w-100">
          <Col>
            <Button variant="secondary" className="w-100" onClick={close}>
              Back
            </Button>
          </Col>
          <Col>
            <Button
              variant="outline-offblack"
              className="w-100"
              onClick={openProfileInNewWindow}
            >
              Open Profile
            </Button>
          </Col>
        </Row>
      </Modal.Footer>
    </Modal>
  );
};

export default NurseActiveNotYetWorkedModal;
